import type { ReactNode } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** A field in the left palette — draggable onto a section/list. */
export function PaletteItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab', userSelect: 'none' }}>
      {children}
    </div>
  );
}

/** A reorderable chip already placed in a section / column list. */
export function SortableChip({ id, selected, onClick, children }: { id: string; selected?: boolean; onClick?(): void; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} onClick={onClick} {...listeners} {...attributes}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, cursor: 'grab',
        border: `1px solid ${selected ? 'var(--sapButton_Selected_BorderColor,#0064d9)' : 'var(--sapField_BorderColor,#ccc)'}`,
        outline: selected ? '2px solid var(--sapContent_FocusColor,#0064d9)' : 'none',
        background: 'var(--sapField_Background,#fff)', borderRadius: '0.5rem', padding: '0.35rem 0.6rem', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
      {children}
    </div>
  );
}

/** A drop target (a section body or the list-columns tray). */
export function DropZone({ id, children, style }: { id: string; children: ReactNode; style?: React.CSSProperties }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} style={{ ...style, outline: isOver ? '2px dashed var(--sapContent_FocusColor,#0064d9)' : '2px dashed transparent', outlineOffset: 2, borderRadius: '0.5rem', transition: 'outline-color .1s' }}>
      {children}
    </div>
  );
}
